import { useQuery } from '@apollo/client'
import { UntitledIcon } from '@faceup/icons'
import { ulCheck } from '@faceup/icons/ulCheck'
import { ulPlus } from '@faceup/icons/ulPlus'
import { usXClose } from '@faceup/icons/usXClose'
import { defineMessages, FormattedMessage } from '@faceup/localization'
import { Navigate } from '@faceup/router'
import { Flex, Typography, useModal, useUiBaseConfig } from '@faceup/ui-base'
import { ChannelPageType } from '@faceup/utils'
import { type ReactNode, useContext, useEffect, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { type MenuItem, MenuItems } from '../../Components/MenuItems/MenuItems'
import { SurveyDataContext } from '../../Contexts/SurveySourceProvider'
import { useIsFilledSurvey } from '../../hooks/useIsFilledSurvey'
import { useSurveyPreview } from '../../hooks/useSurveyPreview'
import { CreateSurveyForm } from './CreateSurveyForm'
import { useFilledPage } from './useFilledPage'

const messages = defineMessages('followUp.pages.survey.surveyView', [
  { id: 'previewModalTitle', defaultMessage: 'This is a preview' },
  {
    id: 'previewModalDescription',
    defaultMessage:
      "Please note, this is just a preview of the survey. Responses can't be saved and the survey can't be submitted.",
  },
  {
    id: 'disabledDescription',
    defaultMessage: 'We are sorry, but this survey is no longer available',
  },
  { id: 'startSurvey', defaultMessage: 'Start' },
] as const)

const fragments = {
  SurveySteps_survey: graphql(`
    fragment SurveySteps_survey on PublicSurvey {
      id
      sourceType
      defaultLanguage
      config {
        id
      }
      institution {
        id
      }
      pages {
        ... on ChannelPage {
          id
          type
          content
          title
        }
      }
      ...CreateSurveyForm_survey
    }
  `),
  SurveySteps_institution: graphql(`
    fragment SurveySteps_institution on PublicCompany {
      id
      ...CreateSurveyForm_institution
    }
  `),
  SurveyNotAvailable_survey: graphql(`
    fragment SurveyNotAvailable_survey on PublicSurvey {
      id
      pages {
        ... on ChannelPage {
          id
          type
          content
          title
        }
      }
    }
  `),
}

const query = graphql(`
  query SurveyViewQuery(
    $reportSourceId: PublicReportSourceGlobalId!
    $channelIdHack: ID!
  ) {
    publicSurvey(reportSourceId: $reportSourceId) {
      id
      defaultLanguage
      isEnabled
      config {
        id
      }
      ...CreateSurveyForm_survey
      ...SurveySteps_survey
      ...SurveyNotAvailable_survey
      institution {
        id
        ...SurveySteps_institution
      }
    }
  }
`)

const SurveyView = () => {
  const { isPreview } = useSurveyPreview()
  const surveyData = useContext(SurveyDataContext)
  const reportSourceId = surveyData?.id ?? ''

  const modal = useModal()
  const [previewParam, setPreviewParam] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isPreview) {
        setPreviewParam(true)
      }
    }, 200)

    return () => clearTimeout(timer)
  }, [isPreview])

  // biome-ignore lint/correctness/useExhaustiveDependencies: modal.confirm will make the modal crop up twice
  useEffect(() => {
    if (previewParam) {
      modal.alert.warning({
        title: <FormattedMessage message={messages.previewModalTitle} />,
        content: <FormattedMessage message={messages.previewModalDescription} />,
      })
    }
  }, [previewParam])

  const { data } = useQuery(query, {
    variables: {
      channelIdHack: reportSourceId,
      reportSourceId,
    },
    skip: !surveyData,
  })

  const survey = data?.publicSurvey
  const institution = survey?.institution

  const [isFilledSurvey] = useIsFilledSurvey(survey?.id ?? '')

  if (!survey || !institution) {
    return null
  }

  if (isFilledSurvey && !isPreview) {
    return <Navigate to={routes => routes.sentSurvey()} replace />
  }

  return (
    <Flex
      style={{ minHeight: '60vh', textAlign: 'center' }}
      align='center'
      justify='center'
      vertical
      gap='20px'
    >
      {survey.isEnabled || isPreview ? (
        <SurveySteps institution={institution} survey={survey} isPreview={isPreview} />
      ) : (
        <SurveyNotAvailable survey={survey} />
      )}
    </Flex>
  )
}

type SurveyStepsProps = {
  survey: FragmentType<typeof fragments.SurveySteps_survey>
  institution: FragmentType<typeof fragments.SurveySteps_institution>
  isPreview?: boolean
}

const SurveySteps = ({
  survey: _survey,
  institution: _institution,
  isPreview = false,
}: SurveyStepsProps) => {
  const survey = getFragmentData(fragments.SurveySteps_survey, _survey)
  const institution = getFragmentData(fragments.SurveySteps_institution, _institution)
  const [currentStep, setCurrentStep] = useState<ChannelPageType>(ChannelPageType.BeforeSend)
  const filledPage = useFilledPage({ pages: survey.pages, step: currentStep })
  const [isFilledSurvey] = useIsFilledSurvey(survey.id)

  if (currentStep === ChannelPageType.BeforeSend) {
    const menuItems: MenuItem[] = [
      ...(!isFilledSurvey || isPreview
        ? [
            {
              key: 'startSurvey',
              onClick: () => setCurrentStep(ChannelPageType.FormItems),
              icon: ulPlus,
              label: <FormattedMessage message={messages.startSurvey} />,
              variant: 'filled' as const,
              dataTest: 'start-survey-button',
            },
          ]
        : []),
    ]

    return (
      <>
        <Typography.Title>{filledPage?.title}</Typography.Title>
        <Typography.Text>{filledPage?.content}</Typography.Text>
        <MenuItems items={menuItems} />
      </>
    )
  }

  if (currentStep === ChannelPageType.FormItems) {
    return (
      <CreateSurveyForm
        isPreview={isPreview}
        survey={survey}
        institution={institution}
        surveyId={survey.id}
        surveyDefaultLanguage={survey.defaultLanguage}
      />
    )
  }

  if (currentStep === ChannelPageType.AfterSend) {
    return (
      <>
        <CircleIcon icon={<UntitledIcon icon={ulCheck} size='40px' color='white' />} />
        <Typography.Title>{filledPage?.title}</Typography.Title>
        <Typography.Text>{filledPage?.content}</Typography.Text>
      </>
    )
  }
  return null
}

type SurveyNotAvailableProps = {
  survey: FragmentType<typeof fragments.SurveyNotAvailable_survey>
}

const SurveyNotAvailable = ({ survey: _survey }: SurveyNotAvailableProps) => {
  const survey = getFragmentData(fragments.SurveyNotAvailable_survey, _survey)
  const filledPage = useFilledPage({
    pages: survey.pages,
    step: ChannelPageType.BeforeSend,
  })

  return (
    <>
      <CircleIcon icon={<UntitledIcon icon={usXClose} size='40px' color='white' />} />
      <Typography.Title>{filledPage?.title}</Typography.Title>
      <Typography.Text>
        <FormattedMessage message={messages.disabledDescription} />
      </Typography.Text>
    </>
  )
}

const CircleIcon = ({ icon }: { icon: ReactNode }) => {
  const { token } = useUiBaseConfig()
  return (
    <Flex
      style={{
        width: '66px',
        height: '66px',
        borderRadius: '50%',
        background: token.colorPrimary,
      }}
      justify='center'
      align='center'
    >
      {icon}
    </Flex>
  )
}

export default SurveyView
